/* Predictive Search Custom Element */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = document.querySelector('.search-drawer__input');
    if (!this.input) return;

    this.resultsContainer = document.getElementById('PredictiveSearchResults');
    this.limit = parseInt(this.dataset.limit) || 6;
    this._debounceTimer = null;

    this.input.addEventListener('input', () => {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.onInput(), 300);
    });
  }

  async onInput() {
    const query = this.input.value.trim();

    if (query.length < 2) {
      if (this.resultsContainer) this.resultsContainer.innerHTML = '';
      return;
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${this.limit}`
      );
      const data = await response.json();
      this.renderResults(data.resources.results);
    } catch (error) {
      console.error('Predictive search error:', error);
    }
  }

  renderResults(results) {
    if (!this.resultsContainer) return;

    let html = '';

    /* Drop tier-gated Insider Lots the current member can't access (graded lots
       with custom.insider_tier above their tier). suggest.json carries no
       metafields, so we match the product handle against window.PACK_INSIDER_TIERS
       (snippets/insider-lots-map.liquid). Mirrors the main search-results gate +
       the graded-auctions band; logged-out/unverified default to hidden. */
    const products = (results.products || []).filter(product =>
      !(window.packInsiderBlocked && window.packInsiderBlocked(product.handle))
    );

    if (products.length > 0) {
      html += '<div class="predictive-search__group predictive-search__group--horizontal">';
      products.forEach(product => {
        const imgSrc = product.featured_image && product.featured_image.url
          ? product.featured_image.url
          : (product.image || '');
        const image = imgSrc
          ? `<img src="${imgSrc}" class="predictive-search__item-image" width="80" height="80" loading="lazy" alt="">`
          : '';
        /* suggest.json returns price as a DOLLAR string (e.g. "60.00"), not cents,
           so format it directly — the old parseInt(price)/100 turned $60 into $0.60. */
        const fallback = product.price ? parseFloat(product.price) : 0;
        const price = product.price ? `$${fallback.toFixed(2)}` : '';

        /* Auction lot? (graded lot with an auction window/bid — flagged in
           window.PACK_AUCTION_LOTS). Show a "Current bid" label + the live bid,
           fetched from Firebase after render. suggest.json carries no metafields,
           hence the handle map. */
        const isAuction = !!(window.PACK_AUCTION_LOTS && window.PACK_AUCTION_LOTS[product.handle]);
        let priceHtml = '';
        if (isAuction) {
          priceHtml = `<span class="predictive-search__item-price predictive-search__item-price--bid" data-predictive-bid data-fallback="${fallback}"><span class="predictive-search__bid-label">Current bid</span>${price}</span>`;
        } else if (price) {
          priceHtml = `<span class="predictive-search__item-price">${price}</span>`;
        }

        html += `
          <a href="${product.url}" class="predictive-search__item"${isAuction ? ` data-predictive-auction-pid="${product.id}"` : ''}>
            ${image}
            <div class="predictive-search__item-info">
              <span class="predictive-search__item-title">${product.title}</span>
              ${priceHtml}
            </div>
          </a>
        `;
      });
      html += '</div>';
    }

    if (!html) {
      html = '<p class="predictive-search__no-results">No results found</p>';
    }

    this.resultsContainer.innerHTML = html;
    this.syncAuctionBids();
  }

  /* Fill each auction result's "Current bid" with the live high bid from
     /auctions/<productId>/bids (same Firebase source as the auction cards +
     featured banner). Uses .once() per render — the dropdown is transient and
     re-renders on every keystroke, so there's nothing to tear down. No-ops if
     Firebase isn't loaded. */
  syncAuctionBids() {
    if (!this.resultsContainer) return;
    if (typeof firebase === 'undefined' || !firebase.database) return;
    const db = firebase.database();
    this.resultsContainer.querySelectorAll('[data-predictive-auction-pid]').forEach(item => {
      const pid = item.getAttribute('data-predictive-auction-pid');
      const priceEl = item.querySelector('[data-predictive-bid]');
      if (!pid || !priceEl) return;
      const fallback = parseFloat(priceEl.getAttribute('data-fallback')) || 0;
      db.ref('auctions/' + pid + '/bids').once('value').then(snap => {
        let high = fallback;
        snap.forEach(c => { const amt = (c.val() || {}).amount || 0; if (amt > high) high = amt; });
        priceEl.innerHTML = '<span class="predictive-search__bid-label">Current bid</span>$' + high.toFixed(2);
      }).catch(() => {});
    });
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
